import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastContext, ToastContextProps } from 'contexts/ToastContext';
import {
  QuestionnaireMainProgressWrapper,
  QuestionnaireMainSliderWrapper,
  QuestionnaireMainWrapper,
} from 'pages/questionnaire/slides/questionnaire/QuestionnaireMain.styled';
import ProgressBar from 'components/common/progress-bar/ProgressBar';
import { SwiperSlide, useSwiper } from 'swiper/react';
import Slider from 'components/slider/Slider';
import QuestionSlideWrapper, {
  QuestionSlideWrapperProps,
} from 'pages/questionnaire/slides/questionnaire/questionnaire-slide/QuestionSlideWrapper';
import { Swiper as SwiperClass } from 'swiper/types';
import useAPIQuestionnaire, { IResponse } from 'hooks/api/useAPIQuestionnaire';
import { Questionnaire } from 'types/app';
import { PRIMARY_QUESTIONNAIRE_NB, TOTAL_QUESTIONNAIRE } from 'constants/global';

export interface QuestionnaireMainProps {
  q: Questionnaire.IQuestionnaire;
  topicNumbers: number;
  onProgressUpdate?: (id: string, current: number, total: number) => void;
  questionnaireProgression: number;
}

const QuestionnaireMain: React.FC<QuestionnaireMainProps> = ({ q, onProgressUpdate, questionnaireProgression }) => {
  const { t } = useTranslation('translation');
  const parentSwiper = useSwiper();
  const { sendResponses, startQuestionTracking, getCurrentTiming } = useAPIQuestionnaire();
  const questionnaire = useRef<Questionnaire.IQuestionnaire>(q);
  const swiperInstance = useRef<SwiperClass>();
  const [slidesContents, setSlidesContents] = useState<Array<QuestionSlideWrapperProps>>([]);
  const { setToast } = useContext(ToastContext) as ToastContextProps;
  // Guards against double taps. A second tap while a response is being sent or
  // while the slider is still animating would call gotoNext twice and silently
  // skip a question.
  const isSending = useRef(false);
  const isSliderAnimating = () => !!swiperInstance.current?.animating;
  const isNavigationLocked = () => isSending.current || isSliderAnimating();

  const updateSlidesContents = () => {
    const slideIndex = swiperInstance.current?.realIndex;
    const theQuestionnaire = questionnaire.current;
    const nextQuestionIndex = theQuestionnaire.currentQuestionIndex + 1;
    const prevQuestionIndex = theQuestionnaire.currentQuestionIndex - 1;
    const currentQuestion = theQuestionnaire.questions[theQuestionnaire.currentQuestionIndex];
    const nextQuestion = theQuestionnaire.questions[nextQuestionIndex];
    const previousQuestion = theQuestionnaire.questions[prevQuestionIndex];

    const slideCurrent: QuestionSlideWrapperProps = {
      question: currentQuestion,
      gotoNext:
        (nextQuestion?.response && nextQuestion.id > 0) || currentQuestion?.response
          ? gotoNext
          : undefined,
      gotoPrevious: previousQuestion?.response && questionnaireProgression !== (PRIMARY_QUESTIONNAIRE_NB * 100 / TOTAL_QUESTIONNAIRE) ? gotoPrevious : undefined,
      onAnswer,
      disableButShowPreviousButton: currentQuestion?.disableButShowPreviousButton
    };

    const slideNext: QuestionSlideWrapperProps = {
      question: nextQuestion,
      gotoNext:
        theQuestionnaire.questions[nextQuestionIndex + 1]?.response || nextQuestion?.response
          ? gotoNext
          : undefined,
      gotoPrevious,
    };

    const slidePrevious: QuestionSlideWrapperProps = {
      question: previousQuestion,
      gotoNext,
      gotoPrevious: theQuestionnaire.questions[prevQuestionIndex - 1] ? gotoNext : undefined,
    };

    if (slideIndex !== undefined && swiperInstance.current) {
      const totalSlides = swiperInstance.current.slides.length;
      const currentSlideIndex = slideIndex;
      const nextSlideIndex = slideIndex + 1 >= totalSlides ? 0 : slideIndex + 1;
      const prevSlideIndex = slideIndex - 1 < 0 ? totalSlides - 1 : slideIndex - 1;
      const contents: Array<QuestionSlideWrapperProps> = [];

      contents[currentSlideIndex] = slideCurrent;
      contents[nextSlideIndex] = slideNext;
      contents[prevSlideIndex] = slidePrevious;

      setSlidesContents(contents);
    }
  };

  const onInitSwiper = (swiper: SwiperClass) => {
    swiperInstance.current = swiper;
    onSwiperSlideChange();
  };

  const onSwiperSlideChange = () => {
    // Create new tracker for current question (type_question: 0 for main questionnaire)
    const currentQuestion = questionnaire.current.questions[questionnaire.current.currentQuestionIndex];
    if (currentQuestion && currentQuestion.id > 0) {
      startQuestionTracking();
    }
    
    updateSlidesContents();
  };

  const gotoPrevious = () => {
    if (isNavigationLocked()) return;
    const prevIndex = questionnaire.current?.currentQuestionIndex - 1;
    questionnaire.current.currentQuestionIndex = prevIndex > 0 ? prevIndex : 0;
    swiperInstance.current?.slidePrev();
  };

  const gotoNext = () => {
    if (isNavigationLocked()) return;
    const nextIndex = questionnaire.current?.currentQuestionIndex + 1;
    if (!questionnaire.current.questions[nextIndex]) {
      // Last question reached: go back to any unanswered question instead of
      // looping on the last one forever.
      gotoFirstUnanswered();
      return;
    }
    questionnaire.current.currentQuestionIndex = nextIndex;
    swiperInstance.current?.slideNext();
  };

  const gotoFirstUnanswered = () => {
    // Section transitions (id < 0) are not questions
    const firstUnansweredIndex = questionnaire.current.questions.findIndex(
      (question) => question.id > 0 && !question.response,
    );
    if (firstUnansweredIndex < 0) {
      // Everything is answered: move on to the next part.
      parentSwiper.slideNext();
      return;
    }
    setToast({ message: t('questionnaire.unanswered-question'), status: 'warning' });
    questionnaire.current.currentQuestionIndex = firstUnansweredIndex;
    swiperInstance.current?.slideNext();
  };

  const updateQuestionnaire = (qAnswered: Questionnaire.IQuestion) => {
    const tmpQnnaire: Questionnaire.IQuestionnaire = { ...questionnaire.current };
    const questions = [...tmpQnnaire.questions];

    questions.forEach((q) => {
      if (q.id === qAnswered.id) {
        q.response = qAnswered.response;
      }
    });

    let nbQAnswered = 0;

    if (q.allQuestions) {
      nbQAnswered = q.allQuestions.filter((item) => {
        return !!item.response && item.response > 0;
      }).length || 0;
    } else {
      nbQAnswered = questions.filter((item) => {
        return !!item.response && item.response > 0;
      }).length || 0;
    }

    tmpQnnaire.progression = Math.floor((nbQAnswered / TOTAL_QUESTIONNAIRE) * 100);

    questionnaire.current = tmpQnnaire;

    if (onProgressUpdate) {
      onProgressUpdate('main', nbQAnswered, TOTAL_QUESTIONNAIRE);
    }
  };

  const onAnswer = async (qAnswered: Questionnaire.IQuestion) => {
    if (isNavigationLocked()) return;

    const timing = getCurrentTiming();
    let isSent = false;
    isSending.current = true;
    try {
      isSent = await sendResponses([
        {
          questionId: qAnswered.id,
          questionnaireId: questionnaire.current.id,
          response: qAnswered.response,
          responseOrder: qAnswered.order,
        },
      ] as Array<IResponse>, timing || undefined);
    } finally {
      isSending.current = false;
    }

    if (!isSent) return;
    updateQuestionnaire(qAnswered);
    if (questionnaire.current.progression >= 100) {
      parentSwiper.slideNext();
    } else {
      gotoNext();
    }
  };

  useEffect(() => {
    updateSlidesContents();
    if (onProgressUpdate) {
      let nbQAnswered = 0;
      if (questionnaire.current.allQuestions) {
        nbQAnswered = questionnaire.current.allQuestions.filter((item) => {
          return !!item.response && item.response > 0;
        }).length || 0;
      } else {
        nbQAnswered = questionnaire.current.questions.filter((item) => {
          return !!item.response && item.response > 0;
        }).length || 0;
      }
      onProgressUpdate('main', nbQAnswered, TOTAL_QUESTIONNAIRE);
    }
  }, []);

  return (
    <QuestionnaireMainWrapper>
      <QuestionnaireMainSliderWrapper>
        <Slider
          initialSlide={1}
          onInit={onInitSwiper}
          allowTouchMove={false}
          onSlideChangeTransitionEnd={onSwiperSlideChange}
          loop
        >
          <SwiperSlide>
            <QuestionSlideWrapper
              {...slidesContents[0]}
              key={slidesContents[0]?.question ? `${slidesContents[0].question.id}` : 0}
            />
          </SwiperSlide>
          <SwiperSlide>
            <QuestionSlideWrapper
              {...slidesContents[1]}
              key={slidesContents[1]?.question ? `${slidesContents[1].question.id}` : 1}
            />
          </SwiperSlide>
          <SwiperSlide>
            <QuestionSlideWrapper
              {...slidesContents[2]}
              key={slidesContents[2]?.question ? `${slidesContents[2].question.id}` : 2}
            />
          </SwiperSlide>
        </Slider>
      </QuestionnaireMainSliderWrapper>
      <QuestionnaireMainProgressWrapper>
        <ProgressBar percent={questionnaireProgression} title={`${t('questionnaire.progression-title')}`} />
      </QuestionnaireMainProgressWrapper>
    </QuestionnaireMainWrapper>
  );
};

export default QuestionnaireMain;
